// CaptureStatusBar — the sticky bottom-bar Mark complete / Reopen toggle
// for the building capture screen, lifted out of building-capture-content.tsx
// to keep that file under the max-lines policy (the repo's split idiom).
//
// The destructive Cancel session affordance lives in the CaptureHero
// top-right (via `headerAction`) so this primary CTA gets the full bar
// width and the destructive control sits visually away from it.
//
// Frozen (admin-only) statuses: the CTA is disabled (audit-1-#7 — the
// server refuses the write), and since audit-7 the read-only
// BuildingStatusBadge renders beside it so a surveyor with a stranded
// session (a transient offline-window state) SEES the building was
// admin-frozen instead of staring at an unexplained disabled button.
// Counting stays enabled — ADR-022: late counts still land (the
// drain/quarantine path protects them).

import type { ReactElement } from 'react'
import { Check, RotateCcw } from 'lucide-react'

import { isAdminOnlyBuildingStatus } from '@citycare/shared'
import type { BuildingStatus } from '@citycare/shared'

import { Button } from '../../ui/button'
import { BuildingStatusBadge } from '../components/building-status-badge'
import { StickyBottomBar } from '../components/sticky-bottom-bar'

export interface CaptureStatusBarProps {
  effectiveStatus: BuildingStatus
  isComplete: boolean
  togglingStatus: boolean
  onToggle: () => void
}

export function CaptureStatusBar({
  effectiveStatus,
  isComplete,
  togglingStatus,
  onToggle,
}: CaptureStatusBarProps): ReactElement {
  const frozen = isAdminOnlyBuildingStatus(effectiveStatus)
  return (
    <StickyBottomBar>
      {frozen ? (
        // Read-only badge, same treatment StartCaptureCard gives the
        // no-session frozen state — the surveyor sees the building was
        // admin-frozen (audit-7). Counting is unaffected.
        <BuildingStatusBadge status={effectiveStatus} />
      ) : null}
      <Button
        variant="primary"
        onClick={onToggle}
        disabled={
          effectiveStatus === 'not_started' || frozen || togglingStatus
        }
        aria-label={isComplete ? 'Reopen building' : 'Mark building complete'}
        className="flex-1 min-h-12 justify-center gap-1.5 rounded-2xl bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-lime)] hover:bg-[color:var(--color-cc-forest)]/90"
      >
        {isComplete ? (
          <RotateCcw aria-hidden="true" className="h-4 w-4" />
        ) : (
          <Check aria-hidden="true" className="h-4 w-4" />
        )}
        {togglingStatus
          ? isComplete
            ? 'Reopening…'
            : 'Marking complete…'
          : isComplete
            ? 'Reopen'
            : 'Mark complete'}
      </Button>
    </StickyBottomBar>
  )
}
